/**
 * DialogTelemed Component - React 19 Migration
 *
 * A reusable dialog/modal component for displaying warnings, errors, and messages.
 *
 * MIGRATION CHANGES FROM REACT 16 TO 19:
 * =====================================
 *
 * 1. MATERIAL-UI v4 → MUI v5 (@mui/material)
 *    - Updated imports from '@material-ui/core' to '@mui/material'
 *
 * 2. STYLED COMPONENTS
 *    - Replaced makeStyles classes with styled components from styles.js
 *    - Cleaner JSX without className props
 *
 * 3. PERFORMANCE
 *    - React.memo to prevent unnecessary re-renders
 *    - PropTypes for runtime validation
 *
 * @param {boolean} open - Controls dialog visibility
 * @param {Function} handleClose - Default close handler
 * @param {Object} dialogConfig - Dialog configuration { title, message, func }
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, DialogContentText } from '@mui/material';
import { useTranslation } from '../../../../shared/hooks';
import * as translations from './intl';
import {
  StyledDialogTitle,
  StyledDialogActions,
  IconImage,
  Description,
  StyledButton,
} from './styles';
import IconWarning from '../../../../assets/icons/ico-alert.png';

function DialogTelemed({ open, handleClose, dialogConfig }) {
  const { locale: intl } = useTranslation({
    translationsFile: translations,
  });

  const { LABEL_ACCEPT } = intl;

  const onClose = dialogConfig?.func || handleClose;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <StyledDialogTitle id="alert-dialog-title">
        {!dialogConfig?.title && (
          <IconImage src={IconWarning} alt="warning icon" />
        )}
        {dialogConfig?.title && <h3>{dialogConfig.title}</h3>}
      </StyledDialogTitle>

      <DialogContent>
        <DialogContentText id="alert-dialog-description" component="div">
          <Description>{dialogConfig?.message}</Description>
        </DialogContentText>
      </DialogContent>

      <StyledDialogActions>
        <StyledButton onClick={onClose} color="primary" text={LABEL_ACCEPT} />
      </StyledDialogActions>
    </Dialog>
  );
}

DialogTelemed.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  dialogConfig: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
    func: PropTypes.func,
  }),
};

export default React.memo(DialogTelemed);
